import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../components/AppLayout';
import PageTitle from '../components/PageTitle';
import api from '../api/api';
import '../styles/pages/ProfileEdit.css';

/**
 * ProfileEdit - プロフィール編集ページ（つむぎデザイン統一）
 * MYPAGE_REDESIGN_V2.md 機能3
 */
const ProfileEdit = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // フォームデータ
  const [formData, setFormData] = useState({
    displayName: '',
    age: '',
    gender: '',
    allergies: [], // アレルギー食材の配列
    allergiesOther: '', // その他のアレルギー（自由入力）
    healthGoals: '',
    avoidFoods: [], // 避けたい食材（選択式 + 自由入力）
    avoidFoodsOther: '' // 避けたい食材（自由入力）
  });

  // 家族構成データ
  const [family, setFamily] = useState({
    hasChildren: false,
    childrenAgeGroups: [],
    familySize: 1
  });

  const [message, setMessage] = useState({ type: '', text: '' });
  const [initialFormData, setInitialFormData] = useState(null); // 初期状態を保存

  useEffect(() => {
    document.title = 'プロフィール編集 | はしもと栄養パートナー';
  }, []);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/profile');
      console.log('=== loadProfile DEBUG ===');
      console.log('API Response:', response.data);
      console.log('Profile Object:', response.data.user?.profile);

      if (response.data.success && response.data.user?.profile) {
        const profile = response.data.user.profile;
        const loadedData = {
          displayName: profile.displayName || '',
          age: profile.age || '',
          gender: profile.gender || '',
          allergies: Array.isArray(profile.allergies) ? profile.allergies : [],
          allergiesOther: profile.allergiesOther || '',
          healthGoals: profile.healthGoals || '',
          avoidFoods: Array.isArray(profile.avoidFoods) ? profile.avoidFoods : [],
          avoidFoodsOther: profile.avoidFoodsOther || ''
        };
        console.log('Loaded Data:', loadedData);
        setFormData(loadedData);
        setInitialFormData(JSON.parse(JSON.stringify(loadedData))); // 初期状態をディープコピー

        // 家族情報を読み込み
        if (response.data.user.family) {
          setFamily(response.data.user.family);
        }
      } else {
        console.warn('Profile data not found in response');
      }
    } catch (error) {
      console.error('プロフィール読み込みエラー:', error);
      setMessage({ type: 'error', text: 'プロフィールの読み込みに失敗しました' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // 家族情報変更ハンドラー
  const handleFamilyChange = (field, value) => {
    setFamily(prev => {
      const updated = { ...prev, [field]: value };
      // 子どもがいない場合は年齢層をクリア
      if (field === 'hasChildren' && !value) {
        updated.childrenAgeGroups = [];
      }
      return updated;
    });
  };

  // 子どもの年齢層トグル
  const toggleChildAgeGroup = (ageGroup) => {
    setFamily(prev => {
      const currentGroups = prev.childrenAgeGroups || [];
      const newGroups = currentGroups.includes(ageGroup)
        ? currentGroups.filter(g => g !== ageGroup)
        : [...currentGroups, ageGroup];
      return { ...prev, childrenAgeGroups: newGroups };
    });
  };

  // 家族情報保存（プロフィール保存時に一緒に実行）
  const saveFamilyInfo = async () => {
    try {
      await api.put('/api/profile/family', family);
    } catch (error) {
      console.error('家族情報保存エラー:', error);
    }
  };

  // チェックボックス用ハンドラー
  const handleCheckboxChange = (field, value, checked) => {
    setFormData(prev => {
      const currentArray = prev[field] || [];
      if (checked) {
        return { ...prev, [field]: [...currentArray, value] };
      } else {
        return { ...prev, [field]: currentArray.filter(item => item !== value) };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // 確認モーダルを表示
    setShowConfirmation(true);
  };

  const handleConfirmSave = async () => {
    try {
      setSaving(true);
      setShowConfirmation(false);
      setMessage({ type: '', text: '' });

      console.log('=== handleConfirmSave DEBUG ===');
      console.log('Saving Data:', formData);
      const response = await api.put('/api/profile', { profile: formData });
      console.log('Save Response:', response.data);

      // 家族情報も保存
      await saveFamilyInfo();

      if (response.data.success) {
        setMessage({ type: 'success', text: '✨ プロフィールを保存しました' });
        // 保存成功時は初期データを更新（戻るボタンで警告が出ないように）
        setInitialFormData(JSON.parse(JSON.stringify(formData)));
        setTimeout(() => {
          navigate('/mypage');
        }, 1500);
      }
    } catch (error) {
      console.error('保存エラー:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'プロフィールの保存に失敗しました'
      });
    } finally {
      setSaving(false);
    }
  };

  // フォームデータが変更されているかチェック
  const hasUnsavedChanges = () => {
    if (!initialFormData) return false;
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  };

  // 戻るボタンのハンドラー（変更確認付き）
  const handleBackClick = () => {
    if (hasUnsavedChanges()) {
      const confirmed = window.confirm('変更が保存されていません。本当に戻りますか？');
      if (confirmed) {
        navigate('/mypage');
      }
    } else {
      navigate('/mypage');
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <PageTitle
          title="プロフィール編集"
          subtitle="読み込み中..."
          icon="✏️"
        />
        <div className="tsumugi-container py-6">
          <div className="tsumugi-card text-center">
            <p className="profile-edit-hint">読み込み中...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageTitle
        title="プロフィール編集"
        subtitle="あなたのことを教えてね"
        backPath="/mypage"
        backLabel="マイページ"
      />

      <div className="tsumugi-container py-6">
        {/* メッセージ表示 */}
        {message.text && (
          <div className={`profile-edit-message profile-edit-message--${message.type}`}>
            {message.text}

            {/* 保存成功時のみSNSシェアボタンを表示 */}
            {message.type === 'success' && (
              <div className="sns-share-section">
                <p className="sns-share-title">
                  🎉 プロフィールを更新したことをシェアしよう！
                </p>
                <div className="sns-share-buttons">
                  {/* Twitterシェアボタン */}
                  <button
                    type="button"
                    onClick={() => {
                      const text = encodeURIComponent('つむぎでプロフィールを更新しました！健康的な食生活を目指して頑張ります✨ #つむぎ #健康管理 #栄養相談');
                      const url = encodeURIComponent(window.location.origin);
                      window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank', 'width=550,height=420');
                    }}
                    className="sns-share-btn sns-share-btn--twitter"
                  >
                    <span>𝕏</span>
                    <span>ポストする</span>
                  </button>

                  {/* LINEシェアボタン */}
                  <button
                    type="button"
                    onClick={() => {
                      const text = encodeURIComponent('つむぎでプロフィールを更新しました！健康的な食生活を目指して頑張ります✨');
                      const url = encodeURIComponent(window.location.origin);
                      window.open(`https://social-plugins.line.me/lineit/share?url=${url}&text=${text}`, '_blank', 'width=550,height=420');
                    }}
                    className="sns-share-btn sns-share-btn--line"
                  >
                    <span>📱</span>
                    <span>LINEでシェア</span>
                  </button>

                  {/* Facebookシェアボタン */}
                  <button
                    type="button"
                    onClick={() => {
                      const url = encodeURIComponent(window.location.origin);
                      window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=550,height=420');
                    }}
                    className="sns-share-btn sns-share-btn--facebook"
                  >
                    <span>f</span>
                    <span>シェア</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* プロフィールフォーム */}
        <form onSubmit={handleSubmit}>
          <div className="tsumugi-card">
            <h3 className="tsumugi-card-title">📝 基本情報</h3>

            <div className="profile-edit-form">
              {/* 表示名 */}
              <div className="profile-edit-field">
                <label className="profile-edit-label">
                  ニックネーム <span className="profile-edit-optional">（任意）</span>
                </label>
                <input
                  type="text"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleChange}
                  placeholder="つむぎ"
                  className="profile-edit-input"
                  maxLength={50}
                />
                <p className="profile-edit-hint">
                  どんな風に呼ばれたいかな？
                </p>
              </div>

              {/* 年齢 */}
              <div className="profile-edit-field">
                <label className="profile-edit-label">
                  年齢 <span className="profile-edit-optional">（任意）</span>
                </label>
                <input
                  type="number"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  placeholder="25"
                  className="profile-edit-input"
                  min="0"
                  max="150"
                />
                <p className="profile-edit-hint">
                  年齢に合わせたアドバイスができるよ
                </p>
              </div>

              {/* 性別 */}
              <div className="profile-edit-field">
                <label className="profile-edit-label">
                  性別 <span className="profile-edit-optional">（任意）</span>
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className="profile-edit-select"
                >
                  <option value="">選択してください</option>
                  <option value="female">女性</option>
                  <option value="male">男性</option>
                  <option value="other">その他</option>
                  <option value="prefer-not-to-say">回答しない</option>
                </select>
                <p className="profile-edit-hint">
                  体調管理のアドバイスに役立つよ
                </p>
              </div>
            </div>
          </div>

          {/* 家族構成（Phase 2-A） */}
          <div className="tsumugi-card">
            <h3 className="tsumugi-card-title">👨‍👩‍👧‍👦 家族構成</h3>
            <p className="profile-edit-hint" style={{ marginBottom: '1rem' }}>
              家族向けのメニュー提案ができるようになるよ
            </p>

            <div className="profile-edit-form">
              {/* 家族人数 */}
              <div className="profile-edit-field">
                <label className="profile-edit-label">
                  家族の人数 <span className="profile-edit-optional">（任意）</span>
                </label>
                <select
                  value={family.familySize}
                  onChange={(e) => handleFamilyChange('familySize', parseInt(e.target.value))}
                  className="profile-edit-select"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                    <option key={n} value={n}>{n}人</option>
                  ))}
                </select>
              </div>

              {/* 子どもの有無 */}
              <div className="profile-edit-field">
                <label className="profile-edit-label">
                  お子さんはいますか？ <span className="profile-edit-optional">（任意）</span>
                </label>
                <div className="family-toggle-options">
                  <button
                    type="button"
                    className={`family-toggle-btn ${!family.hasChildren ? 'family-toggle-btn--selected' : ''}`}
                    onClick={() => handleFamilyChange('hasChildren', false)}
                  >
                    いない
                  </button>
                  <button
                    type="button"
                    className={`family-toggle-btn ${family.hasChildren ? 'family-toggle-btn--selected' : ''}`}
                    onClick={() => handleFamilyChange('hasChildren', true)}
                  >
                    いる
                  </button>
                </div>
              </div>

              {/* 子どもの年齢層（子どもがいる場合のみ表示） */}
              {family.hasChildren && (
                <div className="profile-edit-field">
                  <label className="profile-edit-label">
                    お子さんの年齢層 <span className="profile-edit-optional">（複数選択可）</span>
                  </label>
                  <div className="child-age-options">
                    {[
                      { value: 'infant', label: '乳児（0-1歳）' },
                      { value: 'toddler', label: '幼児（2-5歳）' },
                      { value: 'elementary', label: '小学生' },
                      { value: 'junior_high', label: '中学生' },
                      { value: 'high_school', label: '高校生以上' }
                    ].map(option => (
                      <button
                        key={option.value}
                        type="button"
                        className={`child-age-btn ${family.childrenAgeGroups?.includes(option.value) ? 'child-age-btn--selected' : ''}`}
                        onClick={() => toggleChildAgeGroup(option.value)}
                      >
                        {family.childrenAgeGroups?.includes(option.value) ? '✓ ' : '+ '}
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <p className="profile-edit-hint">
                    お子さんの年齢に合わせたレシピを提案できるよ
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* 健康情報 */}
          <div className="tsumugi-card">
            <h3 className="tsumugi-card-title">🍀 健康について</h3>

            <div className="profile-edit-form">
              {/* アレルギー */}
              <div className="profile-edit-field">
                <label className="profile-edit-label">
                  🚨 アレルギー食材 <span className="profile-edit-optional">（任意）</span>
                </label>
                <p className="profile-edit-hint allergy-section-title">
                  アレルギーがある食材をクリックで選択してください
                </p>

                {/* 主なアレルギー食材 */}
                <div className="allergy-section">
                  <p className="allergy-section-title">
                    主なアレルギー食材（タップで選択）:
                  </p>
                  <div className="allergy-button-group">
                    {['卵', '乳（牛乳）', '小麦', 'えび', 'かに', '落花生（ピーナッツ）', 'そば', '魚類'].map((allergen) => (
                      <button
                        key={allergen}
                        type="button"
                        onClick={() => handleCheckboxChange('allergies', allergen, !formData.allergies.includes(allergen))}
                        className={`allergy-button ${formData.allergies.includes(allergen) ? 'allergy-button--selected' : ''}`}
                      >
                        {formData.allergies.includes(allergen) ? '✓ ' : '+ '}{allergen}
                      </button>
                    ))}
                  </div>
                </div>

                {/* その他のアレルギー食材（コンパクト表示） */}
                <div className="allergy-section">
                  <p className="allergy-section-title">
                    その他のアレルギー食材:
                  </p>
                  <details className="allergy-details">
                    <summary className="allergy-details-summary">
                      ▶ その他の食材を表示
                    </summary>
                    <div className="allergy-details-content">
                      <div className="allergy-details-grid">
                        {[
                          'あわび', 'いか', 'いくら', 'オレンジ', 'カシューナッツ', 'キウイフルーツ',
                          '牛肉', 'くるみ', 'ごま', 'さけ', 'さば', '大豆', '鶏肉', 'バナナ',
                          '豚肉', 'まつたけ', 'もも', 'やまいも', 'りんご', 'ゼラチン'
                        ].map((allergen) => (
                          <button
                            key={allergen}
                            type="button"
                            onClick={() => handleCheckboxChange('allergies', allergen, !formData.allergies.includes(allergen))}
                            className={`allergy-button-small ${formData.allergies.includes(allergen) ? 'allergy-button-small--selected' : ''}`}
                          >
                            {formData.allergies.includes(allergen) ? '✓ ' : '+ '}{allergen}
                          </button>
                        ))}
                      </div>
                    </div>
                  </details>
                </div>

                {/* 選択されたアレルギー食材の表示 */}
                {formData.allergies.length > 0 && (
                  <div className="selected-items">
                    <p className="selected-items-title">
                      選択中のアレルギー食材:
                    </p>
                    <div className="selected-items-list">
                      {formData.allergies.map((allergy, index) => (
                        <div
                          key={index}
                          className="selected-item"
                        >
                          🚨 {allergy}
                          <button
                            type="button"
                            onClick={() => handleCheckboxChange('allergies', allergy, false)}
                            className="selected-item-remove"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* その他（自由入力） */}
                <div className="other-input-section">
                  <label className="other-input-label">
                    その他のアレルギー食材（手動入力）
                  </label>
                  <input
                    type="text"
                    name="allergiesOther"
                    value={formData.allergiesOther}
                    onChange={handleChange}
                    placeholder="その他の食材を入力"
                    className="profile-edit-input"
                    maxLength={200}
                  />
                </div>
              </div>

              {/* 避けたい食材 */}
              <div className="profile-edit-field">
                <label className="profile-edit-label">
                  ❌ 避けたい食材 <span className="profile-edit-optional">（任意）</span>
                </label>
                <p className="profile-edit-hint allergy-section-title">
                  アレルギーではないが避けたい食材（嫌いな食べ物、宗教的制限など）
                </p>

                {/* 包括的なカテゴリー選択 */}
                <div className="allergy-section">
                  <p className="allergy-section-title">
                    よく避けられる食材カテゴリー（タップで選択）:
                  </p>
                  <div className="allergy-button-group">
                    {[
                      { label: '肉類全般', value: '肉類全般' },
                      { label: '魚類全般', value: '魚類全般' },
                      { label: '乳製品全般', value: '乳製品全般' },
                      { label: '卵全般', value: '卵全般' },
                      { label: '動物性食品全般（ヴィーガン）', value: '動物性食品全般（ヴィーガン）' },
                      { label: '豚肉（ハラール）', value: '豚肉（ハラール）' },
                      { label: '豚肉・貝類（コーシャ）', value: '豚肉・貝類（コーシャ）' },
                      { label: '化学調味料全般', value: '化学調味料全般' },
                      { label: '人工甘味料', value: '人工甘味料' },
                      { label: '保存料・防腐剤', value: '保存料・防腐剤' },
                      { label: '着色料', value: '着色料' },
                      { label: '香料', value: '香料' },
                      { label: 'トランス脂肪酸', value: 'トランス脂肪酸' },
                      { label: '白砂糖', value: '白砂糖' },
                      { label: 'グルテン', value: 'グルテン' }
                    ].map((category) => (
                      <button
                        key={category.value}
                        type="button"
                        onClick={() => handleCheckboxChange('avoidFoods', category.value, !formData.avoidFoods.includes(category.value))}
                        className={`avoid-food-button ${formData.avoidFoods.includes(category.value) ? 'avoid-food-button--selected' : ''}`}
                      >
                        {formData.avoidFoods.includes(category.value) ? '✓ ' : '+ '}{category.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 選択された避けたい食材の表示 */}
                {formData.avoidFoods.length > 0 && (
                  <div className="selected-items">
                    <p className="selected-items-title">
                      選択中の避けたい食材:
                    </p>
                    <div className="selected-items-list">
                      {formData.avoidFoods.map((food, index) => (
                        <div
                          key={index}
                          className="selected-avoid-item"
                        >
                          ❌ {food}
                          <button
                            type="button"
                            onClick={() => handleCheckboxChange('avoidFoods', food, false)}
                            className="selected-avoid-item-remove"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* その他（自由入力） */}
                <div className="other-input-section">
                  <label className="other-input-label">
                    その他避けたい食材（手動入力）
                  </label>
                  <input
                    type="text"
                    name="avoidFoodsOther"
                    value={formData.avoidFoodsOther}
                    onChange={handleChange}
                    placeholder="例：レバー、チーズ、パクチー"
                    className="profile-edit-input"
                    maxLength={200}
                  />
                </div>
              </div>

              {/* 健康目標 */}
              <div className="profile-edit-field">
                <label className="profile-edit-label">
                  健康目標 <span className="profile-edit-optional">（任意）</span>
                </label>
                <textarea
                  name="healthGoals"
                  value={formData.healthGoals}
                  onChange={handleChange}
                  placeholder="例：体調を整えたい、栄養バランスを良くしたい"
                  className="profile-edit-textarea"
                  rows="3"
                  maxLength={500}
                />
                <p className="profile-edit-hint">
                  どんなことを目指してるか教えてね
                </p>
              </div>
            </div>
          </div>

          {/* 保存ボタン */}
          <div className="profile-edit-actions">
            <button
              type="button"
              className="tsumugi-btn tsumugi-btn-outline"
              onClick={handleBackClick}
              disabled={saving}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="tsumugi-btn tsumugi-btn-primary"
              disabled={saving}
            >
              {saving ? '保存中...' : '✨ 保存する'}
            </button>
          </div>
        </form>

        {/* 確認モーダル */}
        {showConfirmation && (
          <div className="confirmation-modal-overlay" onClick={() => setShowConfirmation(false)}>
            <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
              <h3 className="confirmation-modal-title">
                📝 保存内容の確認
              </h3>

              <div className="confirmation-modal-content">
                {/* 基本情報 */}
                {(formData.displayName || formData.age || formData.gender) && (
                  <div className="confirmation-modal-section">
                    <h4 className="confirmation-modal-section-title">📋 基本情報</h4>
                    <div className="confirmation-modal-section-content">
                      {formData.displayName && <div>ニックネーム: {formData.displayName}</div>}
                      {formData.age && <div>年齢: {formData.age}歳</div>}
                      {formData.gender && <div>性別: {formData.gender}</div>}
                    </div>
                  </div>
                )}

                {/* アレルギー食材 */}
                {(formData.allergies.length > 0 || formData.allergiesOther) && (
                  <div className="confirmation-modal-section">
                    <h4 className="confirmation-modal-section-title">🚨 アレルギー食材</h4>
                    <div className="confirmation-modal-section-content">
                      {formData.allergies.length > 0 && <div>{formData.allergies.join('、')}</div>}
                      {formData.allergiesOther && <div>その他: {formData.allergiesOther}</div>}
                    </div>
                  </div>
                )}

                {/* 避けたい食材 */}
                {(formData.avoidFoods.length > 0 || formData.avoidFoodsOther) && (
                  <div className="confirmation-modal-section">
                    <h4 className="confirmation-modal-section-title">❌ 避けたい食材</h4>
                    <div className="confirmation-modal-section-content">
                      {formData.avoidFoods.length > 0 && <div>{formData.avoidFoods.join('、')}</div>}
                      {formData.avoidFoodsOther && <div>その他: {formData.avoidFoodsOther}</div>}
                    </div>
                  </div>
                )}

                {/* 健康目標 */}
                {formData.healthGoals && (
                  <div className="confirmation-modal-section">
                    <h4 className="confirmation-modal-section-title">🎯 健康目標</h4>
                    <div className="confirmation-modal-section-content">
                      {formData.healthGoals}
                    </div>
                  </div>
                )}
              </div>

              <div className="confirmation-modal-buttons">
                <button
                  type="button"
                  className="tsumugi-btn tsumugi-btn-outline confirmation-modal-button"
                  onClick={() => setShowConfirmation(false)}
                >
                  修正する
                </button>
                <button
                  type="button"
                  className="tsumugi-btn tsumugi-btn-primary confirmation-modal-button"
                  onClick={handleConfirmSave}
                  disabled={saving}
                >
                  {saving ? '保存中...' : '✨ 確認して保存'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 注意事項 */}
        <div className="tsumugi-card info-card">
          <h3 className="tsumugi-card-title">💡 プロフィールについて</h3>
          <div className="profile-edit-notes">
            <p className="profile-edit-note">
              📌 入力した情報は、より適切なアドバイスを提供するために使用します
            </p>
            <p className="profile-edit-note">
              🔒 プライバシーは厳重に保護されます
            </p>
            <p className="profile-edit-note">
              ✨ すべての項目は任意です。無理せず、答えられる範囲で大丈夫だよ
            </p>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default ProfileEdit;
